import { useEffect, useMemo, useRef, useState } from "react";
import { describeCategories } from "../../../shared/categories";
import { leafletMapHtml, type TravelMode } from "../../../shared/places";
import { PLACE_KIND_LABELS } from "../../../shared/plans";
import {
  clampKm,
  fetchRouteGeometry,
  googleMapsRouteVia,
  planSemiRoute,
  type SemiRoutePlan,
  type SemiStop,
} from "../../../shared/semiRoute";
import { CategoryFilter } from "./CategoryFilter";
import { PointPicker, type PickedPoint } from "./PointPicker";

const MODES: { key: TravelMode; label: string }[] = [
  { key: "car", label: "En coche" },
  { key: "bike", label: "En bici" },
  { key: "walk", label: "A pie" },
  { key: "run", label: "Corriendo" },
];

let nextId = 1;
const newStop = (): SemiStop => ({ id: `s${nextId++}`, categories: [], fromKm: null });

const fmtKm = (km: number) => `${km.toFixed(km >= 10 ? 0 : 1).replace(".", ",")} km`;
const fmtMin = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}` : `${m} min`);

/**
 * Ruta semipersonalizada: origen → destino con las paradas que el usuario quiera, cada una con sus categorías
 * y el km a partir del cual quiere pararse. Lo que se deje en blanco se decide solo (al azar).
 */
export function SemiRoutePanel({ gps }: { gps: { latitude: number; longitude: number } | null }) {
  const [origin, setOrigin] = useState<PickedPoint | null>(null);
  const [originGps, setOriginGps] = useState(true);
  const [dest, setDest] = useState<PickedPoint | null>(null);
  const [mode, setMode] = useState<TravelMode>("car");
  const [stops, setStops] = useState<SemiStop[]>([]);
  const [baseKm, setBaseKm] = useState<number | null>(null);
  const [baseMin, setBaseMin] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("Elige origen y destino, añade tus paradas y pulsa «Trazar ruta».");
  const [plan, setPlan] = useState<SemiRoutePlan | null>(null);
  const [planPoints, setPlanPoints] = useState<{ origin: PickedPoint; dest: PickedPoint } | null>(null);
  const gen = useRef(0);

  const effectiveOrigin: PickedPoint | null = originGps ? (gps ? { ...gps, label: "Mi ubicación" } : null) : origin;

  // Distancia total: en cuanto hay origen y destino, para saber entre qué km se puede elegir.
  useEffect(() => {
    const o = effectiveOrigin;
    if (!o || !dest) {
      setBaseKm(null);
      setBaseMin(null);
      return;
    }
    const mine = ++gen.current;
    const timer = setTimeout(() => {
      void fetchRouteGeometry([o, dest], mode).then((g) => {
        if (mine !== gen.current) return;
        setBaseKm(Math.round(g.distanceKm * 10) / 10);
        setBaseMin(g.durationMin);
      });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrigin?.latitude, effectiveOrigin?.longitude, dest?.latitude, dest?.longitude, mode]);

  const update = (id: string, patch: Partial<SemiStop>) => setStops((list) => list.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const trace = async () => {
    const o = effectiveOrigin;
    if (!o) {
      setStatus(originGps ? "Aún no tengo tu ubicación GPS. Actívala o elige otro origen." : "Elige el origen.");
      return;
    }
    if (!dest) {
      setStatus("Elige el destino.");
      return;
    }
    const mine = ++gen.current;
    setBusy(true);
    setPlan(null);
    setStatus("Calculando la ruta y buscando paradas por el camino…");
    try {
      const result = await planSemiRoute({ origin: o, destination: dest, mode, stops });
      if (mine !== gen.current) return;
      setPlan(result);
      setPlanPoints({ origin: o, dest });
      const missing = result.unmatched.length;
      setStatus(
        `Ruta de ${fmtKm(result.totalKm)} · ${fmtMin(result.travelMinutes)} de viaje${result.dwellMinutes ? ` + ~${fmtMin(result.dwellMinutes)} en las paradas` : ""}.` +
          (missing ? ` ${missing} parada(s) sin encontrar: revisa sus avisos.` : "") +
          (result.final.approximate ? " (Sin conexión con el servicio de rutas: distancias aproximadas.)" : ""),
      );
    } catch {
      if (mine === gen.current) setStatus("No pude trazar la ruta ahora. Reintenta en unos segundos.");
    } finally {
      if (mine === gen.current) setBusy(false);
    }
  };

  const ordered = useMemo(
    () => (plan ? plan.stops.filter((r) => r.place).sort((a, b) => (a.atKm ?? 0) - (b.atKm ?? 0)) : []),
    [plan],
  );
  const mapHtml = useMemo(() => {
    if (!plan || !planPoints) return null;
    return leafletMapHtml(
      planPoints.origin,
      { ...planPoints.dest, name: planPoints.dest.label || "Destino" },
      ordered.map((r) => ({ latitude: r.place!.latitude, longitude: r.place!.longitude, name: r.place!.name })),
      [],
      { line: plan.final.points, originLabel: planPoints.origin.label || "Origen" },
    );
  }, [plan, planPoints, ordered]);
  const mapsUrl = plan && planPoints ? googleMapsRouteVia(planPoints.origin, planPoints.dest, ordered.map((r) => r.place!), mode) : null;

  return (
    <section className="semi-route">
      <p className="lead">
        Pon el origen y el destino, añade las paradas que quieras y decide qué tipo de sitio y a partir de qué km. Lo que dejes en blanco se elige al azar.
      </p>
      <PointPicker title="Desde (origen)" value={origin} onChange={setOrigin} gps={gps} allowGps usingGps={originGps} onUseGps={setOriginGps} />
      <PointPicker
        title="Hasta (destino)"
        value={dest}
        onChange={setDest}
        gps={gps}
        allowGps={false}
        usingGps={false}
        onUseGps={() => undefined}
        placeholder="Ciudad o dirección (ej. Barcelona)"
      />
      <p className="label">Cómo vas</p>
      <div className="time-row">
        {MODES.map((m) => (
          <button key={m.key} type="button" className={`time-chip${mode === m.key ? " on" : ""}`} onClick={() => setMode(m.key)}>
            {m.label}
          </button>
        ))}
      </div>
      {baseKm != null ? (
        <p className="point-ok">
          Distancia directa: {fmtKm(baseKm)}
          {baseMin != null ? ` · ${fmtMin(baseMin)}` : ""}
        </p>
      ) : null}

      <p className="label">Paradas ({stops.length})</p>
      {stops.map((stop, index) => (
        <StopCard
          key={stop.id}
          index={index}
          stop={stop}
          totalKm={baseKm}
          onChange={(patch) => update(stop.id, patch)}
          onRemove={() => setStops((list) => list.filter((s) => s.id !== stop.id))}
        />
      ))}
      <button type="button" className="btn-ghost add-stop" onClick={() => setStops((list) => [...list, newStop()])}>
        ＋ Añadir parada
      </button>

      <button type="button" className="btn-copper" onClick={() => void trace()} disabled={busy}>
        {busy ? "Trazando…" : "Trazar ruta"}
      </button>
      <p className="hero-meta" aria-live="polite">
        {status}
      </p>

      {plan ? (
        <>
          <ol className="surprise-stops">
            {plan.stops.map((r, i) => (
              <li key={r.stopId}>
                <span className="stop-n">{i + 1}</span>
                <div>
                  {r.place ? (
                    <>
                      <h3>{r.place.name}</h3>
                      <p>
                        {PLACE_KIND_LABELS[r.place.kind] || r.place.kind} · km {String(r.atKm).replace(".", ",")} (pedido desde el km{" "}
                        {String(r.requestedKm).replace(".", ",")})
                        {r.detourMeters != null && r.detourMeters > 50 ? ` · a ${r.detourMeters} m del camino` : ""}
                      </p>
                    </>
                  ) : (
                    <p className="point-warn">{r.note}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
          {mapHtml ? (
            <div className="map-frame">
              <iframe
                key={`${plan.final.points.length}-${plan.totalKm}`}
                title="Ruta semipersonalizada"
                srcDoc={mapHtml}
                sandbox="allow-scripts allow-same-origin"
                loading="eager"
              />
            </div>
          ) : null}
          {mapsUrl ? (
            <a className="btn-copper map-open" href={mapsUrl} target="_blank" rel="noreferrer">
              Abrir la ruta en Google Maps
            </a>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function StopCard({
  index,
  stop,
  totalKm,
  onChange,
  onRemove,
}: {
  index: number;
  stop: SemiStop;
  totalKm: number | null;
  onChange: (patch: Partial<SemiStop>) => void;
  onRemove: () => void;
}) {
  const auto = stop.fromKm == null;
  const set = (km: number) => onChange({ fromKm: totalKm ? clampKm(km, totalKm) : Math.max(1, km) });
  const quick: Array<[string, number]> = totalKm
    ? [
        ["Al empezar", 1],
        ["¼", totalKm * 0.25],
        ["Mitad", totalKm * 0.5],
        ["¾", totalKm * 0.75],
        ["Casi al final", Math.max(1, totalKm - 3)],
      ]
    : [];
  return (
    <div className="stop-card">
      <div className="stop-card-head">
        <h3>
          Parada {index + 1}
          {stop.categories.length ? ` · ${describeCategories(stop.categories)}` : " · al azar"}
        </h3>
        <button type="button" className="link-btn danger" onClick={onRemove} aria-label={`Quitar la parada ${index + 1}`}>
          Quitar
        </button>
      </div>
      <CategoryFilter compact title="Qué tipo de sitio (opcional)" selected={stop.categories} onChange={(categories) => onChange({ categories })} />
      <p className="label">A partir del km{totalKm ? ` (1 – ${String(Math.round(totalKm * 10) / 10).replace(".", ",")})` : ""}</p>
      <div className="km-row">
        <button type="button" className={`time-chip${auto ? " on" : ""}`} onClick={() => onChange({ fromKm: null })}>
          Automático
        </button>
        {totalKm ? (
          <input
            type="range"
            min={1}
            max={Math.max(1, Math.round(totalKm * 10) / 10)}
            step={0.5}
            value={stop.fromKm ?? totalKm / 2}
            aria-label={`Km de la parada ${index + 1}`}
            onChange={(e) => set(Number(e.target.value))}
          />
        ) : null}
        <input
          type="number"
          className="km-input"
          min={1}
          step={0.5}
          placeholder="auto"
          value={auto ? "" : stop.fromKm ?? ""}
          onChange={(e) => (e.target.value === "" ? onChange({ fromKm: null }) : onChange({ fromKm: Number(e.target.value) }))}
          onBlur={() => {
            if (stop.fromKm != null && totalKm) onChange({ fromKm: clampKm(stop.fromKm, totalKm) });
          }}
        />
        <span>km</span>
      </div>
      {quick.length ? (
        <div className="time-row">
          {quick.map(([label, km]) => (
            <button key={label} type="button" className="time-chip" onClick={() => set(km)}>
              {label}
            </button>
          ))}
        </div>
      ) : (
        <p className="muted">Pon origen y destino para ver los km totales y elegir entre ellos.</p>
      )}
    </div>
  );
}
